import Checkbox from '@orn/shared-web/components/Checkbox';
import { PhoneNumberInput, usePhoneNumberInput } from '@orn/shared-web/components/Input';
import MessagingCard from '@orn/shared-web/components/MessagingCard';
import { BellIcon, LockIcon, PhoneIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { useUpdateUser } from '../../../hooks/mutations/useUpdateUser';
import { useCurrentSession } from '../../../hooks/queries/useCurrentSession';
import LegalLinks from '../../LegalLinks';
import { OnboardingTitle } from '../../OnboardingLayout';
import { useOnboardingContext } from '../OnboardingContext';

import OnboardingStepActions from './OnboardingStepActions';

import type { IOnboardingStepProps } from '../types';

const HELPER_ITEMS = [
  { icon: <PhoneIcon />, label: 'Your phone will be used to send and receive information' },
  { icon: <BellIcon />, label: 'Customize notification preferences anytime in settings' },
  { icon: <LockIcon />, label: 'Your phone number is encrypted and never shared' },
];

/**
 * Phone-number entry screen for the new onboarding flow. Captures the user's
 * phone number and persists it via `updateUser`, then advances to
 * {@link PhoneNumberVerifyStep} — which owns the SMS OTP lifecycle (send,
 * resend, verify, skip). The SMS-consent checkbox is required by A2P 10DLC
 * compliance and gates the CTA.
 *
 * The consent checkbox state survives back-nav round-trips via
 * {@link useOnboardingContext}: the E.164 number the user consented for
 * lives outside this component, so a back-arrow → forward round-trip with the
 * same number restores the checked state. Editing the input to a different
 * E.164 clears it — fresh number, fresh consent.
 *
 * Figma: Mantle - UX/UI, node 4145:68299 ("Onboarding Step 04").
 */
const PhoneNumberEntryStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { session } = useCurrentSession();
  const [updateUser] = useUpdateUser();
  const { consentedPhoneE164, setConsentedPhoneE164 } = useOnboardingContext();

  const initialPhoneNumber = session?.user.phoneNumber ?? '';
  const { value, validation, handleChange } = usePhoneNumberInput(initialPhoneNumber, true);
  const currentE164 = validation.e164Value;
  // Restore checked state when the typed value matches the previously-consented
  // E.164 — covers the back-arrow → forward round-trip case. Drops back to
  // unchecked when the user edits to a different number.
  const smsConsentChecked = consentedPhoneE164 !== null && currentE164 === consentedPhoneE164;
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleConsentChange = React.useCallback(
    (next: boolean) => {
      setConsentedPhoneE164(next ? currentE164 : null);
    },
    [currentE164, setConsentedPhoneE164]
  );

  const canSubmit = validation.isValid && Boolean(value) && smsConsentChecked && !isSubmitting;

  const handleContinue = React.useCallback(async () => {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    try {
      const e164 = currentE164 || value;
      await updateUser({ variables: { input: { phoneNumber: e164 } } });
      onNext();
    } catch (error) {
      logger.error('Failed to submit phone number', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, currentE164, onNext, updateUser, value]);

  return (
    <>
      <OnboardingTitle
        title="Your phone number"
        subtitle="Add your phone number to receive critical alerts from Mantle."
        onBack={onBack}
      />

      <div className="flex w-full flex-col gap-4">
        <PhoneNumberInput value={value} onChange={handleChange} isRequired validation={validation} />

        <MessagingCard variant="mist">
          <MessagingCard.List items={HELPER_ITEMS} />
        </MessagingCard>

        {/* SMS consent — required for A2P 10DLC compliance. */}
        <Checkbox
          isSelected={smsConsentChecked}
          onChange={handleConsentChange}
          size="sm"
          label={
            <span className="text-tertiary text-xs">
              By checking this box, you agree to receive transactional and informational SMS messages from Mantle about
              your account. You also agree to Mantle&apos;s <LegalLinks />. Message and data rates may apply. Reply STOP
              to opt out.
            </span>
          }
        />
      </div>

      <OnboardingStepActions
        isContinueDisabled={!canSubmit}
        isContinueLoading={isSubmitting}
        onContinue={() => void handleContinue()}
      />
    </>
  );
};

PhoneNumberEntryStep.displayName = 'PhoneNumberEntryStep';

export default PhoneNumberEntryStep;
